import { PickOrderAssigneesQuery } from '../../../graphql'

export type AssigneeOption = {
	id: string,
	/** The person, for prose — a toast reads better without an address in it. */
	name: string,
	email: string | null,
}

type AssigneeNode = PickOrderAssigneesQuery['users']['edges'][number]['node']

/**
 * People who can be handed a pick order, from inventory-api's `users` over
 * `core.users`.
 */
export function toAssigneeOptions( nodes: AssigneeNode[] = [] ): AssigneeOption[] {
	return nodes.map( ( node ) => ( {
		id: String( node.id ),
		name: `${node.firstName ?? ''} ${node.lastName ?? ''}`.trim(),
		email: node.emailAddress ?? null,
	} ) )
}

/** The Auth0 profile claims this needs, named so a caller can see the shape. */
export type AssigneeProfile = {
	name?: string | null,
	givenName?: string | null,
	familyName?: string | null,
	email?: string | null,
}

/**
 * The signed-in person as an assignee, or null if their id is not on the token.
 *
 * Built from the token rather than looked up, because the id is the only part
 * that has to be right — it is what the mutation sends — and it is already in
 * hand. Asking inventory-api to name someone the browser can already name would
 * put a round trip in front of a dialog that opens on a click.
 *
 * `given_name`/`family_name` are preferred over `name`: Auth0 falls back to the
 * email address for `name` when no display name is set, which would render the
 * address twice.
 */
export function toCurrentUserOption(
	userId: string | null,
	profile: AssigneeProfile | null | undefined,
): AssigneeOption | null {
	if ( !userId ) {
		return null
	}
	const fullName = [ profile?.givenName, profile?.familyName ].filter( Boolean ).join( ' ' ).trim()
	const email = profile?.email ?? null
	const name = fullName || ( profile?.name && profile.name !== email ? profile.name : '' )
	return { id: userId, name: name.trim(), email }
}

/**
 * How an assignee reads in the picker.
 *
 * Two people at a branch can share a name; their email address is what tells
 * them apart, so it is shown alongside rather than only on hover. Someone with
 * neither a name nor an address on file is named by their address or, failing
 * that, their id — never rendered as an empty row.
 */
export function assigneeOptionLabel( option: AssigneeOption ): string {
	if ( !option.name ) {
		return option.email ?? option.id
	}
	return option.email ? `${option.name} (${option.email})` : option.name
}
